import { useState } from "react";
import { useAsyncHandler } from "../../../../hooks/useAsyncHandler";
import { useSelection, type Selection } from "../../../../hooks/useSelection";
import {
  submitWaitlistSignup,
  WAITLIST_TOPIC_IDS,
  type WaitlistSignup,
  type WaitlistTopicId,
} from "../../../../lib/waitlist";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether `email` looks like an address worth sending. */
export function isValidWaitlistEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

interface UseWaitlistFormResult {
  email: string;
  setEmail: (v: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  topics: Selection<WaitlistTopicId>;
  /** True once a field was left, so errors show only after the person has had a go. */
  emailInvalid: boolean;
  canSubmit: boolean;
  submitting: boolean;
  submitted: boolean;
  error: string | null;
  handleSubmit: () => Promise<void>;
}

/** The waitlist form's state; `send` defaults to the real sign-up so tests can swap it. */
export function useWaitlistForm(
  send: (signup: WaitlistSignup) => Promise<void> = submitWaitlistSignup
): UseWaitlistFormResult {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const topics = useSelection<WaitlistTopicId>();

  const emailValid = isValidWaitlistEmail(email);
  const canSubmit = emailValid && firstName.trim() !== "" && lastName.trim() !== "";

  const { loading, error, handle } = useAsyncHandler(async () => {
    await send({
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      topics: WAITLIST_TOPIC_IDS.filter((id) => topics.isSelected(id)),
    });
    setSubmitted(true);
  });

  async function handleSubmit() {
    setEmailTouched(true);
    if (!canSubmit) return;
    await handle();
  }

  return {
    email,
    setEmail: (v) => {
      setEmail(v);
      setEmailTouched(true);
    },
    firstName,
    setFirstName,
    lastName,
    setLastName,
    topics,
    emailInvalid: emailTouched && !emailValid,
    canSubmit,
    submitting: loading,
    submitted,
    error,
    handleSubmit,
  };
}
